import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react';

// Mirrors MAX_UPLOAD_BYTES in src/server.mjs: the largest document sent inline
// with a message. Every model step re-sends inline files, so larger documents
// belong in the company Library, where Poneva reads them page by page.
export const INLINE_ATTACHMENT_MAX_BYTES = 4 * 1024 * 1024;

function readDataUrl(file: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// PDFs reach the model as a file part, which it reads natively (text and page
// images). The data URL declares application/pdf whatever the browser guessed,
// because the server trusts the data URL's own media type.
export class PdfAttachmentAdapter implements AttachmentAdapter {
  accept = 'application/pdf,.pdf';

  async add({ file }: { file: File }): Promise<PendingAttachment> {
    return {
      id: file.name,
      type: 'document',
      name: file.name,
      contentType: 'application/pdf',
      file,
      status: { type: 'requires-action', reason: 'composer-send' },
    };
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const url = await readDataUrl(attachment.file);
    return {
      ...attachment,
      status: { type: 'complete' },
      content: [
        {
          type: 'file',
          data: `data:application/pdf;base64${url.slice(url.indexOf(','))}`,
          mimeType: 'application/pdf',
          filename: attachment.name,
        },
      ],
    };
  }

  async remove() {}
}

// Refuses documents too large to send inline before they are read. `onTooLarge`
// explains the alternative and returns true when it did; otherwise the add fails.
export class InlineSizeLimitAdapter implements AttachmentAdapter {
  private readonly inner: AttachmentAdapter;
  private readonly onTooLarge: (file: File) => boolean;

  constructor(inner: AttachmentAdapter, onTooLarge: (file: File) => boolean) {
    this.inner = inner;
    this.onTooLarge = onTooLarge;
  }

  get accept() {
    return this.inner.accept;
  }

  async *add(state: { file: File }): AsyncGenerator<PendingAttachment, void> {
    if (state.file.size > INLINE_ATTACHMENT_MAX_BYTES) {
      if (this.onTooLarge(state.file)) return;
      throw new Error(`${state.file.name} is larger than 4 MB.`);
    }
    const added = this.inner.add(state);
    if (Symbol.asyncIterator in added) yield* added;
    else yield await added;
  }

  send(attachment: PendingAttachment) {
    return this.inner.send(attachment);
  }

  remove(attachment: Parameters<AttachmentAdapter['remove']>[0]) {
    return this.inner.remove(attachment);
  }
}

export function inlineAttachmentTooLarge(name: string) {
  return `${name} is larger than 4 MB, too large to send with a message. Choose Add company context (@), then Upload a company file: it is saved to the Library and attached, and Poneva reads it page by page.`;
}
